import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isImageFile, isPdfFile } from './uploadItemUtils';

function formatBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function SortableFileCard({
  item,
  index,
  disabled,
  onRemove,
  onPreview,
  onRotate,
  rotatingId,
  studio,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 2 : 1,
  };

  const file = item.file;
  const isPdf = isPdfFile(file);
  const isImg = isImageFile(file);
  const busy = rotatingId === item.id;

  const openPreview = (e) => {
    e.stopPropagation();
    if (disabled || isDragging || busy) return;
    onPreview?.(item);
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 6 : studio ? 1 : 0}
      {...attributes}
      {...listeners}
      sx={{
        position: 'relative',
        width: studio ? 168 : 132,
        flexShrink: 0,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isDragging ? '#e53935' : studio ? 'rgba(0,0,0,0.08)' : 'rgba(25,118,210,0.2)',
        bgcolor: '#fff',
        overflow: 'hidden',
        cursor: disabled ? 'default' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        boxShadow: studio ? '0 2px 10px rgba(0,0,0,0.06)' : undefined,
        '&:active': { cursor: disabled ? 'default' : 'grabbing' },
      }}
    >
      <IconButton
        size="small"
        aria-label="remover arquivo"
        disabled={disabled || busy}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.(item.id);
        }}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 3,
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: 1,
          width: 28,
          height: 28,
          '&:hover': { bgcolor: '#fff', color: '#d32f2f' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {isPdf && onRotate && (
        <Tooltip title="Girar 90°">
          <IconButton
            size="small"
            aria-label="girar arquivo"
            disabled={disabled || busy}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRotate?.(item);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 3,
              bgcolor: 'rgba(255,255,255,0.95)',
              boxShadow: 1,
              width: 28,
              height: 28,
              '&:hover': { bgcolor: '#fff', color: '#e53935' },
            }}
          >
            <RotateRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      <Box
        onClick={openPreview}
        title="Clique para visualizar"
        sx={{
          height: studio ? 180 : 150,
          bgcolor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: onPreview ? 'pointer' : undefined,
        }}
      >
        {item.thumbLoading || busy ? (
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        ) : item.thumbnailUrl ? (
          <Box
            component="img"
            src={item.thumbnailUrl}
            alt={file.name}
            draggable={false}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', bgcolor: '#fff' }}
          />
        ) : (
          <Box sx={{ textAlign: 'center', color: '#94a3b8', p: 1 }}>
            {isPdf ? (
              <PictureAsPdfIcon sx={{ fontSize: 40, color: '#d32f2f' }} />
            ) : isImg ? (
              <ImageOutlinedIcon sx={{ fontSize: 40 }} />
            ) : (
              <CloudUploadOutlinedIcon sx={{ fontSize: 40 }} />
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 1, py: 0.75 }} onClick={openPreview}>
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          title={file.name}
          sx={{ display: 'block', lineHeight: 1.3 }}
        >
          {index + 1}. {file.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          {formatBytes(file.size)}
        </Typography>
      </Box>
    </Paper>
  );
}

function AddMoreCard({ disabled, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={() => !disabled && onClick?.()}
      sx={{
        width: 132,
        height: 198,
        flexShrink: 0,
        borderRadius: 2,
        border: '2px dashed',
        borderColor: 'rgba(25,118,210,0.35)',
        bgcolor: 'rgba(248,250,252,0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': disabled
          ? undefined
          : { borderColor: '#1976d2', bgcolor: 'rgba(25,118,210,0.06)' },
      }}
    >
      <AddIcon sx={{ fontSize: 36, color: '#1976d2' }} />
      <Typography variant="caption" fontWeight={700} color="primary.main" textAlign="center" px={1}>
        Adicionar
      </Typography>
    </Paper>
  );
}

/**
 * Dropzone ARCA com preview em cards + reordenação (dnd-kit).
 * variant="studio" → layout tipo iLovePDF (grade + botão + flutuante).
 */
export default function UploadDropzone({
  accept = 'application/pdf,.pdf',
  multiple = true,
  maxSizeMb = 50,
  disabled = false,
  onFiles,
  onFilesAdd,
  onReorder,
  onRemove,
  onPreview,
  onRotate,
  rotatingId,
  onSortAlpha,
  items,
  label = 'Arraste arquivos aqui ou clique para selecionar',
  hint,
  showFileStrip = true,
  variant = 'strip',
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const controlled = Array.isArray(items);
  const list = controlled ? items : [];
  const studio = variant === 'studio';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const processList = useCallback(
    (fileList) => {
      setLocalError('');
      const arr = Array.from(fileList || []);
      if (!arr.length) return;
      const maxBytes = maxSizeMb * 1024 * 1024;
      const tooBig = arr.find((f) => f.size > maxBytes);
      if (tooBig) {
        setLocalError(`Arquivo "${tooBig.name}" excede ${maxSizeMb}MB.`);
        return;
      }
      const payload = multiple ? arr : arr.slice(0, 1);
      if (onFilesAdd) onFilesAdd(payload);
      else onFiles?.(payload);
    },
    [maxSizeMb, multiple, onFiles, onFilesAdd]
  );

  const ids = useMemo(() => list.map((i) => i.id), [list]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const oldIndex = list.findIndex((i) => i.id === active.id);
    const newIndex = list.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(list, oldIndex, newIndex));
  };

  const handlePreview = (item) => {
    onPreview?.(item);
    setPreviewItem(item);
  };

  useEffect(() => {
    if (!previewItem?.file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(previewItem.file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewItem]);

  const emptyZone = (
    <Paper
      elevation={0}
      onDragEnter={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        processList(e.dataTransfer.files);
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        border: '2px dashed',
        borderColor: dragOver ? '#e53935' : 'rgba(0,0,0,0.12)',
        bgcolor: dragOver ? 'rgba(229,57,53,0.06)' : studio ? '#f3f4f6' : 'rgba(248,250,252,0.9)',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
        minHeight: studio ? 320 : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: '#e53935', mb: 1 }} />
      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
        {label}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {hint}
        </Typography>
      )}
      <Button
        variant="contained"
        size="small"
        color="error"
        sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
      >
        Selecionar arquivos
      </Button>
    </Paper>
  );

  const fileCards = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={studio ? rectSortingStrategy : horizontalListSortingStrategy}>
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (disabled) return;
            if (e.dataTransfer?.files?.length) processList(e.dataTransfer.files);
          }}
          sx={
            studio
              ? {
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
                  gap: 2,
                  p: 2,
                  alignContent: 'start',
                  minHeight: 360,
                  bgcolor: '#eceff1',
                  borderRadius: 2,
                }
              : {
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1.5,
                  overflowX: 'auto',
                  pb: 1,
                  pt: 0.5,
                  px: 0.5,
                  scrollSnapType: 'x mandatory',
                  '& > *': { scrollSnapAlign: 'start' },
                }
          }
        >
          {list.map((item, index) => (
            <SortableFileCard
              key={item.id}
              item={item}
              index={index}
              disabled={disabled}
              onRemove={onRemove}
              onPreview={handlePreview}
              onRotate={onRotate}
              rotatingId={rotatingId}
              studio={studio}
            />
          ))}
          {!studio && (multiple || list.length === 0) && (
            <AddMoreCard disabled={disabled} onClick={() => inputRef.current?.click()} />
          )}
        </Box>
      </SortableContext>
    </DndContext>
  );

  return (
    <Box sx={{ position: 'relative', height: studio ? '100%' : undefined }}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        disabled={disabled}
        onChange={(e) => {
          processList(e.target.files);
          e.target.value = '';
        }}
      />

      {controlled && showFileStrip && list.length > 0 ? (
        <Box sx={{ position: 'relative', height: studio ? '100%' : undefined }}>
          {!studio && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Arraste os cards para reordenar · {list.length} arquivo{list.length === 1 ? '' : 's'}
            </Typography>
          )}
          {fileCards}

          {studio && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                zIndex: 4,
              }}
            >
              <Tooltip title="Adicionar mais arquivos" placement="left">
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    disabled={disabled}
                    onClick={() => inputRef.current?.click()}
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: '#e53935',
                      color: '#fff',
                      boxShadow: 3,
                      '&:hover': { bgcolor: '#c62828' },
                      '&.Mui-disabled': { bgcolor: '#ef9a9a', color: '#fff' },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      left: -4,
                      minWidth: 22,
                      height: 22,
                      px: 0.5,
                      borderRadius: 10,
                      bgcolor: '#111',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {list.length}
                  </Box>
                </Box>
              </Tooltip>
              {onSortAlpha && (
                <Tooltip title="Ordenar A–Z" placement="left">
                  <IconButton
                    disabled={disabled || list.length < 2}
                    onClick={onSortAlpha}
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: '#fff',
                      boxShadow: 2,
                      '&:hover': { bgcolor: '#fafafa' },
                    }}
                  >
                    <SortByAlphaIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      ) : (
        emptyZone
      )}

      {localError && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {localError}
        </Typography>
      )}

      <Dialog
        open={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {previewItem?.file?.name || 'Visualizar documento'}
          <IconButton
            aria-label="fechar"
            onClick={() => setPreviewItem(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#525659', minHeight: 480 }}>
          {previewUrl && isPdfFile(previewItem?.file) ? (
            <Box
              component="iframe"
              title="preview-pdf"
              src={previewUrl}
              sx={{ width: '100%', height: 520, border: 0, bgcolor: '#525659' }}
            />
          ) : previewUrl && isImageFile(previewItem?.file) ? (
            <Box
              component="img"
              src={previewUrl}
              alt={previewItem?.file?.name}
              sx={{ maxWidth: '100%', maxHeight: 520, display: 'block', mx: 'auto' }}
            />
          ) : (
            <Typography color="common.white" sx={{ p: 3 }}>
              Pré-visualização indisponível.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
